/**
 * ComprovantePDF.jsx
 *
 * 3 formatos: "bobina" | "cupom" | "a4"
 *
 * Props de venda:
 *   venda.subCliente       → { nome, cpf_cnpj?, telefone?, endereco? } | null
 *   venda.ocultarValores   → boolean — quando true omite colunas de valor por completo
 */

import React from "react";
import {
  Document, Page, Text, View, StyleSheet, Image,
} from "@react-pdf/renderer";

// ─── Paleta ───────────────────────────────────────────────────────────────────

const C = {
  primary: "#1A56DB",
  dark:    "#0F2557",
  text:    "#1F2937",
  muted:   "#6B7280",
  border:  "#D1D5DB",
  light:   "#EBF3FF",
  white:   "#FFFFFF",
  success: "#059669",
  danger:  "#DC2626",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v) =>
  `R$ ${Number(v ?? 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const fmtDate = () =>
  new Date().toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

const calcSubtotal = (itens) =>
  itens.reduce((acc, i) => acc + (i.valor_venda ?? 0) * i.quantidade, 0);

// ─── Divisor tracejado ────────────────────────────────────────────────────────

const Divisor = ({ style }) => (
  <Text style={[{ fontSize: 8, color: C.muted, textAlign: "center", marginVertical: 4, letterSpacing: 1 }, style]}>
    {"- - - - - - - - - - - - - - - - - - - - - -"}
  </Text>
);

// ══════════════════════════════════════════════════════════════════════════════
// BOBINA — 58mm térmica
// ══════════════════════════════════════════════════════════════════════════════

const sB = StyleSheet.create({
  page:           { width: 164, paddingHorizontal: 8, paddingVertical: 10, fontFamily: "Courier", backgroundColor: C.white },
  logo:           { width: 48, height: 48, alignSelf: "center", marginBottom: 6 },
  estab:          { fontSize: 10, fontWeight: "bold", textAlign: "center", color: C.dark },
  info:           { fontSize: 7, textAlign: "center", color: C.muted, marginTop: 1 },
  titulo:         { fontSize: 9, fontWeight: "bold", textAlign: "center", color: C.dark, marginTop: 4, textTransform: "uppercase", letterSpacing: 1 },
  data:           { fontSize: 7, textAlign: "center", color: C.muted, marginTop: 2 },
  // Sub-cliente
  scBox:          { borderWidth: 0.5, borderColor: C.primary, borderRadius: 3, padding: 5, marginBottom: 4 },
  scLabel:        { fontSize: 6, fontWeight: "bold", color: C.primary, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 },
  scNome:         { fontSize: 8, fontWeight: "bold", color: C.dark },
  scInfo:         { fontSize: 7, color: C.muted, marginTop: 1 },
  // Itens
  itemNome:       { fontSize: 8, color: C.text, flex: 1, flexWrap: "wrap" },
  itemRow:        { flexDirection: "row", justifyContent: "space-between", marginBottom: 1 },
  itemDetalhe:    { fontSize: 8, color: C.muted, marginLeft: 2 },
  itemTotal:      { fontSize: 8, fontWeight: "bold", color: C.text, minWidth: 42, textAlign: "right" },
  // Totais / pagamentos
  row:            { flexDirection: "row", justifyContent: "space-between", marginBottom: 1 },
  label:          { fontSize: 7, color: C.muted, marginBottom: 1 },
  pgForma:        { fontSize: 8, color: C.text },
  pgValor:        { fontSize: 8, fontWeight: "bold", color: C.text },
  totalLabel:     { fontSize: 11, fontWeight: "bold", color: C.dark, flex: 1 },
  totalValor:     { fontSize: 11, fontWeight: "bold", color: C.primary },
  trocoLabel:     { fontSize: 8, color: C.muted },
  trocoValor:     { fontSize: 8, fontWeight: "bold", color: C.success },
  rodape:         { fontSize: 7, textAlign: "center", color: C.muted, marginTop: 6, fontStyle: "italic" },
});

const ComprovanteBobina = ({ venda, cliente }) => {
  const ocultar   = Boolean(venda.ocultarValores);
  const subtotal  = calcSubtotal(venda.itens);
  const desconto  = Number(venda.desconto ?? 0);
  const total     = subtotal - desconto;
  const sc        = venda.subCliente ?? null;

  return (
    <Page size={{ width: 164, height: "auto" }} style={sB.page}>
      {cliente?.logo_url && <Image style={sB.logo} src={cliente.logo_url} />}
      <Text style={sB.estab}>{cliente?.nome ?? "Estabelecimento"}</Text>
      {cliente?.cnpj_cpf && <Text style={sB.info}>CNPJ/CPF: {cliente.cnpj_cpf}</Text>}
      {cliente?.endereco  && <Text style={sB.info}>{cliente.endereco}</Text>}
      {cliente?.telefone  && <Text style={sB.info}>Tel: {cliente.telefone}</Text>}

      <Divisor />
      <Text style={sB.titulo}>Comprovante de Venda</Text>
      <Text style={sB.data}>{fmtDate()}</Text>

      {/* Sub-cliente */}
      {sc && (
        <View style={sB.scBox}>
          <Text style={sB.scLabel}>Cliente</Text>
          <Text style={sB.scNome}>{sc.nome}</Text>
          {sc.cpf_cnpj  && <Text style={sB.scInfo}>CPF/CNPJ: {sc.cpf_cnpj}</Text>}
          {sc.telefone  && <Text style={sB.scInfo}>Tel: {sc.telefone}</Text>}
          {sc.endereco  && <Text style={sB.scInfo}>{sc.endereco}</Text>}
        </View>
      )}

      <Divisor />

      {/* Itens */}
      {venda.itens.map((item, i) => (
        <View key={i} style={{ marginBottom: 3 }}>
          <Text style={sB.itemNome}>{item.produto_nome}</Text>
          {!ocultar && (
            <View style={sB.itemRow}>
              <Text style={sB.itemDetalhe}>{item.quantidade} x {fmt(item.valor_venda)}</Text>
              <Text style={sB.itemTotal}>{fmt(item.valor_venda * item.quantidade)}</Text>
            </View>
          )}
          {ocultar && (
            <Text style={sB.itemDetalhe}>Qtd: {item.quantidade}</Text>
          )}
        </View>
      ))}

      <Divisor />

      {/* Totais — só se não ocultar */}
      {!ocultar && (
        <>
          {desconto > 0 && (
            <>
              <View style={sB.row}><Text style={sB.pgForma}>Subtotal</Text><Text style={sB.pgValor}>{fmt(subtotal)}</Text></View>
              <View style={sB.row}><Text style={[sB.pgForma, { color: C.danger }]}>Desconto</Text><Text style={[sB.pgValor, { color: C.danger }]}>- {fmt(desconto)}</Text></View>
            </>
          )}
          <View style={[sB.row, { marginTop: 4 }]}>
            <Text style={sB.totalLabel}>TOTAL</Text>
            <Text style={sB.totalValor}>{fmt(total)}</Text>
          </View>
          <Divisor />
          <Text style={sB.label}>Pagamentos:</Text>
          {venda.pagamentos?.map((p, i) => (
            <View key={i} style={sB.row}>
              <Text style={sB.pgForma}>{p.forma}</Text>
              <Text style={sB.pgValor}>{fmt(p.valor)}</Text>
            </View>
          ))}
          {Number(venda.troco) > 0 && (
            <View style={[sB.row, { marginTop: 2 }]}>
              <Text style={sB.trocoLabel}>Troco</Text>
              <Text style={sB.trocoValor}>{fmt(venda.troco)}</Text>
            </View>
          )}
        </>
      )}

      {/* Se ocultar: só forma de pagamento sem valor */}
      {ocultar && venda.pagamentos?.length > 0 && (
        <>
          <Divisor />
          <Text style={sB.label}>Forma de pagamento:</Text>
          {venda.pagamentos.map((p, i) => (
            <Text key={i} style={sB.pgForma}>{p.forma}</Text>
          ))}
        </>
      )}

      <Divisor />
      <Text style={sB.rodape}>Não possui valor fiscal</Text>
      <Text style={sB.rodape}>Obrigado pela preferência!</Text>
    </Page>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// CUPOM — 80mm
// ══════════════════════════════════════════════════════════════════════════════

const sC = StyleSheet.create({
  page:     { width: 227, paddingHorizontal: 12, paddingVertical: 14, fontFamily: "Courier", backgroundColor: C.white },
  logo:     { width: 56, height: 56, alignSelf: "center", marginBottom: 8 },
  cab:      { textAlign: "center", marginBottom: 6 },
  nome:     { fontSize: 12, fontWeight: "bold", color: C.dark },
  sub:      { fontSize: 8, color: C.muted, marginTop: 1 },
  titulo:   { fontSize: 10, fontWeight: "bold", textAlign: "center", color: C.dark, marginVertical: 2, textTransform: "uppercase", letterSpacing: 1.5 },
  fiscal:   { fontSize: 7.5, textAlign: "center", color: C.danger, fontWeight: "bold", borderWidth: 0.5, borderColor: C.danger, borderRadius: 3, paddingVertical: 2, paddingHorizontal: 4, marginVertical: 3, alignSelf: "center" },
  data:     { fontSize: 8, textAlign: "center", color: C.muted, marginBottom: 2 },
  // Sub-cliente
  scBox:    { borderWidth: 0.5, borderColor: C.primary, borderRadius: 3, padding: 6, marginVertical: 4 },
  scLabel:  { fontSize: 6.5, fontWeight: "bold", color: C.primary, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 },
  scNome:   { fontSize: 9, fontWeight: "bold", color: C.dark },
  scInfo:   { fontSize: 7.5, color: C.muted, marginTop: 1 },
  // Tabela
  thRow:    { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: C.border, paddingBottom: 3, marginBottom: 3 },
  thDesc:   { fontSize: 7.5, fontWeight: "bold", color: C.muted, flex: 1 },
  thQtd:    { fontSize: 7.5, fontWeight: "bold", color: C.muted, width: 24, textAlign: "center" },
  thUnit:   { fontSize: 7.5, fontWeight: "bold", color: C.muted, width: 50, textAlign: "right" },
  thTotal:  { fontSize: 7.5, fontWeight: "bold", color: C.muted, width: 52, textAlign: "right" },
  tdRow:    { flexDirection: "row", paddingVertical: 2, borderBottomWidth: 0.3, borderBottomColor: "#EEEEEE" },
  tdDesc:   { fontSize: 8, color: C.text, flex: 1, flexWrap: "wrap" },
  tdQtd:    { fontSize: 8, color: C.muted, width: 24, textAlign: "center" },
  tdUnit:   { fontSize: 8, color: C.muted, width: 50, textAlign: "right" },
  tdTotal:  { fontSize: 8, fontWeight: "bold", color: C.text, width: 52, textAlign: "right" },
  // Totais
  totBlock: { marginTop: 6, paddingTop: 4, borderTopWidth: 0.5, borderTopColor: C.border },
  totRow:   { flexDirection: "row", justifyContent: "space-between", marginBottom: 2 },
  totLabel: { fontSize: 8, color: C.muted },
  totValor: { fontSize: 8, color: C.text },
  grandLbl: { fontSize: 12, fontWeight: "bold", color: C.dark },
  grandVal: { fontSize: 12, fontWeight: "bold", color: C.primary },
  // Pagamentos
  pgBlock:  { marginTop: 6, paddingTop: 4, borderTopWidth: 0.5, borderTopColor: C.border },
  pgTitulo: { fontSize: 8, fontWeight: "bold", color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 },
  pgRow:    { flexDirection: "row", justifyContent: "space-between", marginBottom: 2 },
  pgForma:  { fontSize: 9, color: C.text },
  pgValor:  { fontSize: 9, fontWeight: "bold", color: C.text },
  trocoLbl: { fontSize: 9, color: C.success },
  trocoVal: { fontSize: 9, fontWeight: "bold", color: C.success },
  rodape:   { textAlign: "center", fontSize: 7.5, color: C.muted, marginTop: 8, fontStyle: "italic" },
});

const ComprovanteCupom = ({ venda, cliente }) => {
  const ocultar  = Boolean(venda.ocultarValores);
  const subtotal = calcSubtotal(venda.itens);
  const desconto = Number(venda.desconto ?? 0);
  const total    = subtotal - desconto;
  const sc       = venda.subCliente ?? null;

  return (
    <Page size={{ width: 227, height: "auto" }} style={sC.page}>
      {cliente?.logo_url && <Image style={sC.logo} src={cliente.logo_url} />}
      <View style={sC.cab}>
        <Text style={sC.nome}>{cliente?.nome ?? "Estabelecimento"}</Text>
        {cliente?.cnpj_cpf && <Text style={sC.sub}>CNPJ/CPF: {cliente.cnpj_cpf}</Text>}
        {cliente?.endereco  && <Text style={sC.sub}>{cliente.endereco}</Text>}
        {cliente?.telefone  && <Text style={sC.sub}>Tel: {cliente.telefone}</Text>}
      </View>

      <Divisor />
      <Text style={sC.titulo}>Comprovante de Venda</Text>
      <Text style={sC.fiscal}>NÃO TEM VALOR FISCAL</Text>
      <Text style={sC.data}>{fmtDate()}</Text>

      {/* Sub-cliente */}
      {sc && (
        <View style={sC.scBox}>
          <Text style={sC.scLabel}>Cliente</Text>
          <Text style={sC.scNome}>{sc.nome}</Text>
          {sc.cpf_cnpj && <Text style={sC.scInfo}>CPF/CNPJ: {sc.cpf_cnpj}</Text>}
          {sc.telefone  && <Text style={sC.scInfo}>Tel: {sc.telefone}</Text>}
          {sc.endereco  && <Text style={sC.scInfo}>{sc.endereco}</Text>}
        </View>
      )}

      <Divisor />

      {/* Cabeçalho tabela — sem colunas de valor quando ocultar */}
      <View style={sC.thRow}>
        <Text style={sC.thDesc}>Produto</Text>
        <Text style={sC.thQtd}>Qtd</Text>
        {!ocultar && <Text style={sC.thUnit}>Unit.</Text>}
        {!ocultar && <Text style={sC.thTotal}>Total</Text>}
      </View>

      {venda.itens.map((item, i) => (
        <View key={i} style={sC.tdRow}>
          <Text style={sC.tdDesc}>{item.produto_nome}</Text>
          <Text style={sC.tdQtd}>{item.quantidade}</Text>
          {!ocultar && <Text style={sC.tdUnit}>{fmt(item.valor_venda)}</Text>}
          {!ocultar && <Text style={sC.tdTotal}>{fmt(item.valor_venda * item.quantidade)}</Text>}
        </View>
      ))}

      {/* Totais */}
      {!ocultar && (
        <View style={sC.totBlock}>
          {desconto > 0 && (
            <>
              <View style={sC.totRow}><Text style={sC.totLabel}>Subtotal</Text><Text style={sC.totValor}>{fmt(subtotal)}</Text></View>
              <View style={sC.totRow}><Text style={[sC.totLabel, { color: C.danger }]}>Desconto</Text><Text style={[sC.totValor, { color: C.danger }]}>- {fmt(desconto)}</Text></View>
            </>
          )}
          <View style={[sC.totRow, { marginTop: 4 }]}>
            <Text style={sC.grandLbl}>TOTAL</Text>
            <Text style={sC.grandVal}>{fmt(total)}</Text>
          </View>
        </View>
      )}

      {/* Pagamentos */}
      <View style={sC.pgBlock}>
        <Text style={sC.pgTitulo}>Pagamentos</Text>
        {venda.pagamentos?.map((p, i) => (
          <View key={i} style={sC.pgRow}>
            <Text style={sC.pgForma}>{p.forma}</Text>
            {!ocultar && <Text style={sC.pgValor}>{fmt(p.valor)}</Text>}
          </View>
        ))}
        {!ocultar && Number(venda.troco) > 0 && (
          <View style={sC.pgRow}>
            <Text style={sC.trocoLbl}>Troco</Text>
            <Text style={sC.trocoVal}>{fmt(venda.troco)}</Text>
          </View>
        )}
      </View>

      <Divisor />
      <Text style={sC.rodape}>Documento sem valor fiscal</Text>
      <Text style={sC.rodape}>Obrigado pela preferência!</Text>
    </Page>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// A4
// ══════════════════════════════════════════════════════════════════════════════

const sA = StyleSheet.create({
  page:       { padding: 36, fontSize: 10, fontFamily: "Helvetica", backgroundColor: C.white, color: C.text },
  header:     { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, paddingBottom: 16, borderBottomWidth: 1.5, borderBottomColor: C.primary },
  hLeft:      { flex: 1 },
  logo:       { width: 60, height: 60, marginBottom: 8 },
  nomeEstab:  { fontSize: 18, fontWeight: "bold", color: C.dark, marginBottom: 2 },
  infoEstab:  { fontSize: 8.5, color: C.muted, marginBottom: 1 },
  hRight:     { alignItems: "flex-end" },
  badge:      { fontSize: 7, fontWeight: "bold", color: C.danger, borderWidth: 0.8, borderColor: C.danger, borderRadius: 3, paddingVertical: 2, paddingHorizontal: 6, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 },
  docTitulo:  { fontSize: 14, fontWeight: "bold", color: C.dark, marginBottom: 3 },
  docData:    { fontSize: 8.5, color: C.muted },
  // Sub-cliente
  scRow:      { flexDirection: "row", marginBottom: 20 },
  scBox:      { flex: 1, backgroundColor: C.light, borderRadius: 6, padding: 12, borderLeftWidth: 3, borderLeftColor: C.primary },
  scLabel:    { fontSize: 7.5, fontWeight: "bold", color: C.primary, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 },
  scNome:     { fontSize: 10, fontWeight: "bold", color: C.dark, marginBottom: 2 },
  scInfo:     { fontSize: 8.5, color: C.muted, marginBottom: 1 },
  // Info grid
  infoGrid:   { flexDirection: "row", gap: 16, marginBottom: 20 },
  infoCol:    { flex: 1, backgroundColor: C.light, borderRadius: 6, padding: 12 },
  infoTitulo: { fontSize: 7.5, fontWeight: "bold", color: C.primary, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 },
  infoRow:    { flexDirection: "row", marginBottom: 3 },
  infoLabel:  { fontSize: 8.5, color: C.muted, width: 70 },
  infoValor:  { fontSize: 8.5, color: C.text, fontWeight: "bold", flex: 1 },
  // Tabela
  tabTitulo:  { fontSize: 9, fontWeight: "bold", color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 },
  thead:      { flexDirection: "row", backgroundColor: C.primary, borderRadius: 4, paddingVertical: 6, paddingHorizontal: 10, marginBottom: 2 },
  thText:     { fontSize: 8, fontWeight: "bold", color: C.white },
  trow:       { flexDirection: "row", paddingVertical: 6, paddingHorizontal: 10, borderBottomWidth: 0.5, borderBottomColor: C.border },
  trowAlt:    { backgroundColor: "#F8FAFF" },
  tdText:     { fontSize: 8.5, color: C.text },
  tdMuted:    { fontSize: 8.5, color: C.muted },
  tdPrimary:  { fontSize: 8.5, fontWeight: "bold", color: C.primary },
  colProd:    { flex: 1 },
  colQtd:     { width: 36, textAlign: "center" },
  colUnit:    { width: 68, textAlign: "right" },
  colTotal:   { width: 72, textAlign: "right" },
  totalFinal: { flexDirection: "row", justifyContent: "space-between", marginTop: 6, paddingTop: 6, borderTopWidth: 1.5, borderTopColor: C.primary },
  totalLblG:  { fontSize: 12, fontWeight: "bold", color: C.dark },
  totalValG:  { fontSize: 12, fontWeight: "bold", color: C.primary },
  footer:     { marginTop: 24, paddingTop: 12, borderTopWidth: 1, borderTopColor: C.border, flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end" },
  footerL:    { fontSize: 8, color: C.muted, fontStyle: "italic" },
  footerR:    { fontSize: 7, color: C.muted, textAlign: "right" },
});

const ComprovanteA4 = ({ venda, cliente }) => {
  const ocultar  = Boolean(venda.ocultarValores);
  const subtotal = calcSubtotal(venda.itens);
  const desconto = Number(venda.desconto ?? 0);
  const total    = subtotal - desconto;
  const sc       = venda.subCliente ?? null;

  return (
    <Page size="A4" style={sA.page}>

      {/* ── Cabeçalho ────────────────────────────────────────────────────── */}
      <View style={sA.header}>
        <View style={sA.hLeft}>
          {cliente?.logo_url && <Image style={sA.logo} src={cliente.logo_url} />}
          <Text style={sA.nomeEstab}>{cliente?.nome ?? "Estabelecimento"}</Text>
          {cliente?.cnpj_cpf && <Text style={sA.infoEstab}>CNPJ/CPF: {cliente.cnpj_cpf}</Text>}
          {cliente?.endereco  && <Text style={sA.infoEstab}>{cliente.endereco}</Text>}
          {cliente?.telefone  && <Text style={sA.infoEstab}>Tel: {cliente.telefone}</Text>}
        </View>
        <View style={sA.hRight}>
          <Text style={sA.badge}>Sem valor fiscal</Text>
          <Text style={sA.docTitulo}>Comprovante de Venda</Text>
          <Text style={sA.docData}>{fmtDate()}</Text>
        </View>
      </View>

      {/* ── Bloco sub-cliente ─────────────────────────────────────────────── */}
      {sc && (
        <View style={[sA.scRow, { marginBottom: 16 }]}>
          <View style={sA.scBox}>
            <Text style={sA.scLabel}>Vendido para</Text>
            <Text style={sA.scNome}>{sc.nome}</Text>
            {sc.cpf_cnpj && <Text style={sA.scInfo}>CPF/CNPJ: {sc.cpf_cnpj}</Text>}
            {sc.telefone  && <Text style={sA.scInfo}>Tel: {sc.telefone}</Text>}
            {sc.endereco  && <Text style={sA.scInfo}>{sc.endereco}</Text>}
          </View>
        </View>
      )}

      {/* ── Resumo + Pagamentos (grid) ─────────────────────────────────────── */}
      <View style={sA.infoGrid}>
        {/* Resumo financeiro — só mostra se não ocultar */}
        <View style={sA.infoCol}>
          <Text style={sA.infoTitulo}>Resumo</Text>
          <View style={sA.infoRow}>
            <Text style={sA.infoLabel}>Itens</Text>
            <Text style={sA.infoValor}>{venda.itens.length}</Text>
          </View>
          {!ocultar && (
            <>
              <View style={sA.infoRow}>
                <Text style={sA.infoLabel}>Subtotal</Text>
                <Text style={sA.infoValor}>{fmt(subtotal)}</Text>
              </View>
              {desconto > 0 && (
                <View style={sA.infoRow}>
                  <Text style={[sA.infoLabel, { color: C.danger }]}>Desconto</Text>
                  <Text style={[sA.infoValor, { color: C.danger }]}>- {fmt(desconto)}</Text>
                </View>
              )}
              <View style={sA.infoRow}>
                <Text style={[sA.infoLabel, { fontWeight: "bold", color: C.dark }]}>Total</Text>
                <Text style={[sA.infoValor, { color: C.primary }]}>{fmt(total)}</Text>
              </View>
            </>
          )}
        </View>

        {/* Pagamentos */}
        <View style={sA.infoCol}>
          <Text style={sA.infoTitulo}>Pagamentos</Text>
          {venda.pagamentos?.map((p, i) => (
            <View key={i} style={sA.infoRow}>
              <Text style={sA.infoLabel}>{p.forma}</Text>
              {!ocultar && <Text style={sA.infoValor}>{fmt(p.valor)}</Text>}
            </View>
          ))}
          {!ocultar && Number(venda.troco) > 0 && (
            <View style={sA.infoRow}>
              <Text style={[sA.infoLabel, { color: C.success }]}>Troco</Text>
              <Text style={[sA.infoValor, { color: C.success }]}>{fmt(venda.troco)}</Text>
            </View>
          )}
        </View>
      </View>

      {/* ── Tabela de itens ───────────────────────────────────────────────── */}
      <Text style={sA.tabTitulo}>Itens da venda</Text>

      <View style={sA.thead}>
        <Text style={[sA.thText, sA.colProd]}>Produto</Text>
        <Text style={[sA.thText, sA.colQtd]}>Qtd</Text>
        {!ocultar && <Text style={[sA.thText, sA.colUnit]}>Unitário</Text>}
        {!ocultar && <Text style={[sA.thText, sA.colTotal]}>Total</Text>}
      </View>

      {venda.itens.map((item, i) => (
        <View key={i} style={[sA.trow, i % 2 === 1 ? sA.trowAlt : {}]}>
          <Text style={[sA.tdText, sA.colProd]}>{item.produto_nome}</Text>
          <Text style={[sA.tdMuted, sA.colQtd]}>{item.quantidade}</Text>
          {!ocultar && <Text style={[sA.tdMuted, sA.colUnit]}>{fmt(item.valor_venda)}</Text>}
          {!ocultar && <Text style={[sA.tdPrimary, sA.colTotal]}>{fmt(item.valor_venda * item.quantidade)}</Text>}
        </View>
      ))}

      {/* Total final — só se não ocultar */}
      {!ocultar && (
        <View style={sA.totalFinal}>
          <Text style={sA.totalLblG}>TOTAL</Text>
          <Text style={sA.totalValG}>{fmt(total)}</Text>
        </View>
      )}

      {/* ── Rodapé ──────────────────────────────────────────────────────────── */}
      <View style={sA.footer}>
        <Text style={sA.footerL}>
          Obrigado pela preferência!{"\n"}
          Este documento não tem valor fiscal.
        </Text>
        <Text style={sA.footerR}>
          {cliente?.nome ?? "FluxoEasy"}{"\n"}
          Gerado em {fmtDate()}
        </Text>
      </View>
    </Page>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

const ComprovantePDF = ({ venda, cliente, formato = "a4" }) => {
  const renderPage = () => {
    switch (formato) {
      case "bobina": return <ComprovanteBobina venda={venda} cliente={cliente} />;
      case "cupom":  return <ComprovanteCupom  venda={venda} cliente={cliente} />;
      default:       return <ComprovanteA4     venda={venda} cliente={cliente} />;
    }
  };
  return <Document title="Comprovante de Venda">{renderPage()}</Document>;
};

export default ComprovantePDF;